import { useContext } from 'react'
import { formatMoney } from 'accounting'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons'

import {
  Box, Button, Line, ListItem, ModalContext, SidebarModal, Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'ConfirmCampaignQuotePricingModal'

type ModalCallbacks = {
  approveCampaignQuote: (payload: HandleCallbackActionParams) => void,
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ConfirmCampaignQuotePricingModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { approveCampaignQuote, closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const { callbacks: payloadCallbacks, validationErrors = [] } = digObject(modalData, MODAL_KEY, {})
  const { approveCampaignQuote: approveFn } = payloadCallbacks || {}

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Confirm Quote" titleSecondLine="Pricing" />

      <SidebarModal.Content>
        <Box flexDirection="column" lineHeight="1.3" padding="large" variant="whiteGrey">
          <Text color="orange" fontSize="small">
            <FontAwesomeIcon icon={faExclamationCircle} /> Items will be discarded
          </Text>

          <Text color="bodyFontLightColor" fontSize="small" marginTop="medium">
            The following items have had a price change since they were added to the quote.
          </Text>

          <Text color="bodyFontLightColor" fontSize="small" marginTop="medium">
            To make sure vendors are quoted the current price for provider products you&apos;ll need to
            adjust the quote by removing and re-adding these items to the quote.
          </Text>

          <Text color="bodyFontLightColor" fontSize="small" marginTop="medium">
            The Ignore & Approve button will <strong>discard these items</strong> and approve the quote.
          </Text>
        </Box>

        <Line style={{ marginBottom: 0 }} />

        {validationErrors && (
          <>
            <Text color="bodyFontLightColor" fontSize="small" marginTop="large">Items to be discarded:</Text>

            {/*
             // @ts-expect-error TS7006 */}
            {validationErrors.map((validationError) => {
              const {
                campaign_quote_item_id,
                current_price_inc_fee,
                options_string,
                original_price_inc_fee,
                title,
              } = validationError

              return (
                <ListItem
                  boxProps={{ borderBottom: '1px dashed', borderColor: 'lineColor', paddingY: 'large' }}
                  key={campaign_quote_item_id}
                >
                  <Box flexDirection="column" justifyContent="center" width="auto">
                    <Text fontSize="small">{title}</Text>
                    <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="small">
                      {options_string || 'No Options'}
                    </Text>
                  </Box>

                  <Box
                    alignItems="flex-end"
                    flexDirection="column"
                    justifyContent="center"
                    marginLeft="auto"
                    width="auto"
                  >
                    <Text fontSize="small">{formatMoney(current_price_inc_fee)}</Text>
                    <Text color="orange" fontSize="xsmall" marginTop="small" style={{ textDecoration: 'line-through' }}>
                      {formatMoney(original_price_inc_fee)}
                    </Text>
                  </Box>
                </ListItem>
              )
            })}
          </>
        )}

      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          // @ts-expect-error TS2322
          ghost
          onClick={() => approveCampaignQuote({
            callbacks: {
              action: approveFn,
              afterAction: closeModal,
            },
            shouldRedirect: true,
            toastText: 'Quote Approved',
          })}
          size="medium"
          style={{ marginRight: 4, width: 'calc(50% - 4px)' }}
        >
          Ignore & Approve
        </Button>

        <Button
          buttonStyle="primaryCreate"
          onClick={closeModal}
          size="medium"
          style={{ marginLeft: 4, width: 'calc(50% - 4px)' }}
          // @ts-expect-error TS2322
          text="Adjust Quote"
        >
          Adjust Quote
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ConfirmCampaignQuotePricingModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
